import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { getCaseStudyBySlug } from '../data/caseStudies';

const CaseStudyMedia = ({ image, video, poster, alt, controls = false, preview = false }) => {
  if (video) {
    return (
      <video
        src={video}
        poster={poster || image}
        muted
        playsInline
        loop
        autoPlay={preview}
        controls={controls}
        preload="metadata"
      >
        {alt}
      </video>
    );
  }

  return <img src={image} alt={alt} />;
};

const SectionEyebrow = ({ label }) => (
  <div className="svc-eyebrow">
    <span className="svc-eyebrow-line" />
    <span className="svc-eyebrow-text">{label}</span>
    <span className="svc-eyebrow-line" />
  </div>
);

const ArticleLayout = ({ study }) => (
  <>
    <section className="cs-article-hero">
      <div className="container">
        <Link to="/case-studies" className="cs-back">
          ← All case studies
        </Link>
        <SectionEyebrow label="Case Study" />
        <span className="cs-tag">{study.industry}</span>
        <h1 className="svc-heading cs-article-title">{study.title}</h1>
        <p className="svc-subtext cs-article-tagline">{study.tagline}</p>
      </div>
    </section>

    <section className="cs-article-media">
      <div className="container">
        {study.videos?.length ? (
          <div className="cs-article-media-pair">
            {study.videos.map((clip) => (
              <div key={clip.src} className="cs-article-hero-image">
                <CaseStudyMedia
                  image={clip.poster}
                  video={clip.src}
                  poster={clip.poster}
                  alt={clip.alt || study.title}
                  controls
                  preview
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="cs-article-hero-image">
            <CaseStudyMedia
              image={study.image}
              video={study.video}
              poster={study.image}
              alt={study.title}
              controls={Boolean(study.video)}
            />
          </div>
        )}
      </div>
    </section>

    <article className="cs-article">
      <div className="container cs-article-inner">
        <section className="cs-block">
          <h2 className="cs-block-title">The Overview</h2>
          <p className="cs-block-text">{study.overview}</p>
        </section>

        <section className="cs-block">
          <h2 className="cs-block-title">Highlights</h2>
          <div className="cs-highlights">
            {study.highlights.map((item) => (
              <div key={item.label} className="cs-highlight">
                <h3 className="cs-highlight-label">{item.label}</h3>
                <p className="cs-highlight-text">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="cs-block">
          <h2 className="cs-block-title">The Problem</h2>
          <p className="cs-block-text">{study.problemIntro}</p>
          <ul className="cs-problem-list">
            {study.problemPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <p className="cs-block-text">{study.problemClose}</p>
        </section>

        <section className="cs-block">
          <h2 className="cs-block-title">Engineering the Solution</h2>
          <p className="cs-block-text">{study.solutionIntro}</p>
          <div className="cs-solution-list">
            {study.solutionSections.map((section) => (
              <div key={section.number} className="cs-solution">
                <div className="cs-solution-header">
                  <span className="cs-solution-number">{section.number}</span>
                  <h3 className="cs-solution-title">{section.title}</h3>
                </div>
                <p className="cs-block-text">{section.body}</p>
                {section.points && (
                  <ul className="cs-problem-list">
                    {section.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                )}
                {section.video && (
                  <div className="cs-solution-image">
                    <CaseStudyMedia
                      image={section.videoPoster}
                      video={section.video}
                      poster={section.videoPoster}
                      alt={section.videoAlt || section.title}
                      controls
                    />
                  </div>
                )}
                {section.image && !section.video && (
                  <div className="cs-solution-image">
                    <img src={section.image} alt={section.imageAlt || section.title} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {study.catalog && (
          <section className="cs-block">
            <h2 className="cs-block-title">{study.catalogTitle || 'Detection Catalog'}</h2>
            {study.catalogIntro && <p className="cs-block-text">{study.catalogIntro}</p>}
            <div className="cs-highlights">
              {study.catalog.map((item) => (
                <div key={item.label} className="cs-highlight">
                  <h3 className="cs-highlight-label">{item.label}</h3>
                  <p className="cs-highlight-text">{item.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {study.metrics && (
          <section className="cs-block">
            <h2 className="cs-block-title">{study.metricsTitle || 'Performance'}</h2>
            <div className="cs-table-wrap">
              <table className="cs-table">
                <thead>
                  <tr>
                    {(study.metricsColumns || ['Performance Metric', 'Traditional Manual Process', 'AI ERP Platform']).map(
                      (column) => (
                        <th key={column}>{column}</th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {study.metrics.map((row) => (
                    <tr key={row.metric}>
                      <td data-label={(study.metricsColumns && study.metricsColumns[0]) || 'Performance Metric'}>
                        {row.metric}
                      </td>
                      <td data-label={(study.metricsColumns && study.metricsColumns[1]) || 'Traditional Manual Process'}>
                        {row.before}
                      </td>
                      <td
                        className="cs-table-after"
                        data-label={(study.metricsColumns && study.metricsColumns[2]) || 'AI ERP Platform'}
                      >
                        {row.after}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {study.architecture && (
          <section className="cs-block">
            <h2 className="cs-block-title">{study.architectureTitle || 'Architecture & Impact Summary'}</h2>
            <div className="cs-table-wrap">
              <table className="cs-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Technical Solution</th>
                    <th>Business Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {study.architecture.map((row) => (
                    <tr key={row.domain}>
                      <td data-label="Domain">{row.domain}</td>
                      <td data-label="Technical Solution">{row.solution}</td>
                      <td data-label="Business Outcome">{row.outcome}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="cs-block">
          <h2 className="cs-block-title">{study.techTitle || 'Built for Modern PropTech'}</h2>
          <div className="cs-tech-grid">
            {study.tech.map((item) => (
              <div key={item.title} className="cs-tech">
                <h3 className="cs-tech-title">{item.title}</h3>
                <p className="cs-tech-text">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="cs-cta">
          <p className="cs-cta-text">Building a similar platform?</p>
          <div className="cs-cta-links">
            {study.productUrl && (
              <a
                href={study.productUrl}
                className="cs-cta-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {study.productLabel || 'Visit product'} →
              </a>
            )}
            <Link to="/contact" className="cs-cta-link">
              Talk to us →
            </Link>
          </div>
        </section>
      </div>
    </article>
  </>
);

const StandardLayout = ({ study }) => (
  <>
    <section className="cs-article-hero">
      <div className="container">
        <Link to="/case-studies" className="cs-back">
          ← All case studies
        </Link>
        <SectionEyebrow label="Case Study" />
        <span className="cs-tag">{study.industry}</span>
        <h1 className="svc-heading cs-article-title">{study.title}</h1>
        <p className="svc-subtext cs-article-tagline">{study.tagline}</p>
      </div>
    </section>

    <section className="cs-article-media">
      <div className="container">
        <div className="cs-article-hero-image cs-article-hero-image--contain">
          <img src={study.image} alt={study.title} />
        </div>
      </div>
    </section>

    <article className="cs-article">
      <div className="container cs-article-inner">
        <section className="cs-block">
          <h2 className="cs-block-title">The challenge</h2>
          <p className="cs-block-text">{study.challenge}</p>
        </section>
        <section className="cs-block">
          <h2 className="cs-block-title">What we built</h2>
          <p className="cs-block-text">{study.solution}</p>
        </section>
        <section className="cs-block">
          <h2 className="cs-block-title">Impact</h2>
          <ul className="cs-problem-list">
            {study.impact.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section className="cs-cta">
          <p className="cs-cta-text">Building a similar platform?</p>
          <Link to="/contact" className="cs-cta-link">
            Talk to us →
          </Link>
        </section>
      </div>
    </article>
  </>
);

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const study = getCaseStudyBySlug(slug);

  if (!study) {
    return <Navigate to="/case-studies" replace />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: study.title,
    description: study.seoDescription,
    image: `https://monkstack.com${study.image}`,
    url: `https://monkstack.com/case-studies/${study.slug}`,
    author: {
      '@type': 'Organization',
      name: 'monkStack',
      url: 'https://monkstack.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'monkStack',
      logo: {
        '@type': 'ImageObject',
        url: 'https://monkstack.com/img/logo_landing.png',
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://monkstack.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Case Studies',
        item: 'https://monkstack.com/case-studies',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: study.shortTitle,
        item: `https://monkstack.com/case-studies/${study.slug}`,
      },
    ],
  };

  return (
    <div className="App cs-page">
      <SEO
        title={study.title}
        description={study.seoDescription}
        url={`https://monkstack.com/case-studies/${study.slug}`}
        image={`https://monkstack.com${study.image}`}
        structuredData={[breadcrumbSchema, articleSchema]}
      />
      <Navbar />
      {study.layout === 'article' ? (
        <ArticleLayout study={study} />
      ) : (
        <StandardLayout study={study} />
      )}
      <Footer />
    </div>
  );
};

export default CaseStudyDetailPage;
